import { Input, InputNumber, Select } from 'antd';
import {
  ISRAELI_BANKS,
  TAX_TYPE_LABELS,
  isValidBusinessId,
  VAT_MODE_LABELS,
  withholdingApplies,
  type StaffProfile,
  type StaffTaxType,
  type VatMode,
} from '@salaries/shared';

// Shared editor for a staff member's tax classification + VAT mode + ניכוי מס
// במקור + bank details. Controlled — used by both the per-staff settings drawer
// and the onboarding wizard. VAT mode only shows for עוסק מורשה; the withholding
// rate only for a dealer (a שכיר is taxed through payroll).
export function StaffTaxBankFields({
  value,
  onChange,
  staffName,
}: {
  value: StaffProfile;
  onChange: (patch: Partial<StaffProfile>) => void;
  // Used as the default/placeholder for the account-holder field.
  staffName?: string;
}) {
  const taxOptions = Object.entries(TAX_TYPE_LABELS).map(([v, label]) => ({ value: v, label }));
  const vatOptions = Object.entries(VAT_MODE_LABELS).map(([v, label]) => ({ value: v, label }));
  const bankField = (
    label: string,
    key: keyof Pick<
      StaffProfile,
      'bank_account_holder' | 'bank_branch' | 'bank_account'
    >,
    dir: 'rtl' | 'ltr' = 'rtl',
    placeholder?: string,
    numericOnly = false,
  ) => (
    <div>
      <div className="text-small text-main-text mb-1">{label}</div>
      <Input
        value={value[key] ?? ''}
        dir={dir}
        placeholder={placeholder}
        inputMode={numericOnly ? 'numeric' : undefined}
        // Branch / account are digits only — strip anything else as it's typed
        // (or pasted) so letters can never be entered or saved.
        onChange={(e) =>
          onChange({ [key]: numericOnly ? e.target.value.replace(/\D/g, '') : e.target.value })
        }
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-small text-main-text mb-1">סוג העסקה</div>
        <Select<StaffTaxType>
          value={value.tax_type}
          options={taxOptions as { value: StaffTaxType; label: string }[]}
          // Switching to שכיר drops any withholding rate — a salaried employee is
          // taxed through payroll, never via ניכוי במקור (the server enforces the
          // same rule, this just keeps the form honest).
          onChange={(v) =>
            onChange(withholdingApplies(v) ? { tax_type: v } : { tax_type: v, withholding_pct: 0 })
          }
          style={{ width: 220 }}
        />
      </div>

      {value.tax_type === 'licensed_dealer' && (
        <div>
          <div className="text-small text-main-text mb-1">מע״מ על השכר</div>
          <Select<VatMode>
            value={value.vat_mode}
            options={vatOptions as { value: VatMode; label: string }[]}
            onChange={(v) => onChange({ vat_mode: v })}
            style={{ width: 260 }}
          />
        </div>
      )}

      {withholdingApplies(value.tax_type) && (
        <div>
          <div className="text-small text-main-text mb-1">ניכוי מס במקור</div>
          <InputNumber
            value={value.withholding_pct}
            min={0}
            max={100}
            step={1}
            precision={2}
            dir="ltr"
            addonAfter="%"
            style={{ width: 160 }}
            onChange={(v) => onChange({ withholding_pct: Number(v ?? 0) })}
          />
          <div className="text-small text-secondary-text mt-1">
            אחוז שינוכה מסכום התשלום (כולל מע״מ) ויועבר לרשות המסים. 0 = ללא ניכוי.
          </div>
        </div>
      )}

      {withholdingApplies(value.tax_type) && (
        <div>
          <div className="text-small text-main-text mb-1">מספר עוסק / ח״פ</div>
          <Input
            value={value.business_id ?? ''}
            dir="ltr"
            inputMode="numeric"
            placeholder="9 ספרות"
            status={value.business_id && !isValidBusinessId(value.business_id) ? 'error' : undefined}
            style={{ width: 220 }}
            onChange={(e) => onChange({ business_id: e.target.value.replace(/\D/g, '') || null })}
          />
          <div className="text-small text-secondary-text mt-1">
            {value.business_id && !isValidBusinessId(value.business_id)
              ? 'מספר העוסק אינו תקין'
              : 'החשבוניות של איש הצוות צריכות להיות מופקות על המספר הזה (נבדק כשבדיקת החשבונית פעילה).'}
          </div>
        </div>
      )}

      <div className="text-label font-medium text-main-text mt-1">פרטי חשבון בנק (לתשלום)</div>
      <div className="grid grid-cols-2 gap-3">
        {bankField('שם בעל החשבון', 'bank_account_holder', 'rtl', staffName)}
        <div>
          <div className="text-small text-main-text mb-1">שם הבנק</div>
          <Select
            showSearch
            allowClear
            value={value.bank_name ?? undefined}
            placeholder="בחר בנק"
            optionFilterProp="label"
            options={ISRAELI_BANKS.map((b) => ({ value: b.name, label: `${b.code} — ${b.name}` }))}
            onChange={(v) => onChange({ bank_name: v ?? null })}
            style={{ width: '100%' }}
          />
        </div>
        {bankField('סניף', 'bank_branch', 'ltr', undefined, true)}
        {bankField('מספר חשבון', 'bank_account', 'ltr', undefined, true)}
      </div>
    </div>
  );
}
